import { createFileRoute, Link } from '@tanstack/react-router';
import {
  useAcceptInvitation,
  useGetInvitation,
  useRejectInvitation,
} from '@/api/organizations/queries';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

export const Route = createFileRoute(
  '/_authenticated/_organization/organization/accept-invitation/$invitationId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { invitationId } = Route.useParams();
  const invitation = useGetInvitation(invitationId);
  const acceptInvitation = useAcceptInvitation();
  const rejectInvitation = useRejectInvitation();

  const handleAccept = () => {
    acceptInvitation.mutate({ invitationId });
  };

  const handleReject = () => {
    rejectInvitation.mutate({ invitationId });
  };

  if (invitation.isPending) {
    return (
      <div className="max-w-md w-full mx-auto mt-8">
        <Card>
          <CardContent className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 dark:border-orange-400 mx-auto mb-4" />
            <p className="text-sm">Loading invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invitation.isError) {
    return (
      <div className="max-w-md w-full mx-auto mt-8">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-red-700 dark:text-red-400">
              Failed to load invitation
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center flex flex-col items-center gap-3">
            <CardDescription className="text-primary">{invitation.error?.message}</CardDescription>

            <Button variant="secondary" className="mt-2" asChild>
              <Link to="/" className="text-sm mt-2">
                Go to Home
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const invitationData = invitation.data;

  return (
    <Card>
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">Organization Invitation</CardTitle>
        <CardDescription>You've been invited to join an organization</CardDescription>
      </CardHeader>

      {invitationData && (
        <CardContent>
          <div className="space-y-4">
            <div>
              <span className="text-sm font-medium text-muted-foreground">Organization:</span>
              <p>{invitationData.organizationName}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-muted-foreground">Role:</span>
              <p className="capitalize">{invitationData.role}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-muted-foreground">Invited by:</span>
              <p>{invitationData.inviterEmail}</p>
            </div>
          </div>
        </CardContent>
      )}

      <CardFooter className="flex gap-3 w-full justify-end">
        <Button
          variant="destructive"
          onClick={handleReject}
          disabled={acceptInvitation.isPending || rejectInvitation.isPending}
        >
          {rejectInvitation.isPending ? 'Rejecting...' : 'Reject'}
        </Button>
        <Button
          onClick={handleAccept}
          disabled={acceptInvitation.isPending || rejectInvitation.isPending}
        >
          {acceptInvitation.isPending ? 'Accepting...' : 'Accept'}
        </Button>
      </CardFooter>
    </Card>
  );
}
